import { VisitorCatEnum } from "@components/GAData/utils/types";
import {
  UserRegistrationStatusEnum,
  UserTypeEnum,
} from "@features/mySph/utils/types";
import { FeedAuthor } from "@routes/Author/utils/types";
import { FeedTag } from "@routes/Keyword/utils/types";
import { TagType } from "@routes/Section/types";
import { Author, ContributorProfile, Section } from "@typings/Cue";
import { textToSlug } from "@util/helpers";

/** Function to get the article author names when fetching data from BE*/
export function getArticleAuthorNamesV2(authors: FeedAuthor[] = []): string {
  const authorList = authors.map((author) => author.name.trim());

  return authorList.join(", ");
}

export function getArticleAuthorNames(
  authors: Author[] = [],
  contributors: ContributorProfile[] = []
) {
  const authorList = [] as string[];

  authors.map((author: Author) => {
    // Check if profile is not empty before adding the author
    if (author.profiles && author.profiles.length > 0) {
      authorList.push(author.name.trim());
    }
  });

  contributors.map((contributor: ContributorProfile) => {
    authorList.push(contributor.content.fields.name.trim());
  });

  return authorList.join(", ");
}

export function getArticleChapter1(section: Section) {
  let chapter1 = "";

  if (section.parent.uniqueName !== "ece_frontpage") {
    chapter1 = section.name;
  }

  return textToSlug(chapter1).replaceAll("-", " ");
}

export function getArticleLevel2(section: Section) {
  let level2 = section.name;

  if (section.parent.uniqueName !== "ece_frontpage") {
    level2 = section.parent.name;
  }

  return textToSlug(level2).replaceAll("-", " ");
}

/**
 * Helper function to get the list of keywords separated by comma
 * @param keywords List of keywords.
 * @returns {string}
 */
export const getArticleKeywords = (keywords: FeedTag[]): string => {
  return keywords
    .filter(({ type }) => type === TagType.Keyword)
    .map(({ name }) => name.toLowerCase())
    .join(", ");
};

/**
 * Helper function to get the story thread from the tags.
 * @param tags The tags of the article.
 * @returns {FeedTag|undefined}
 */
export const getArticleStoryThread = (tags: FeedTag[]): FeedTag | undefined => {
  return tags.find(({ type }) => type === TagType.StoryThread);
};

/**
 * Helper function to get the visitor category value.
 * @param userType UserType of the user
 * @param registrationStatus Regustration status of the user
 * @param serviceType Service type of the user
 * @returns {number}
 */
export const getVisitorCat = (
  userType?: UserTypeEnum,
  registrationStatus?: string,
  serviceFlags?: string[]
): number => {
  if (userType === UserTypeEnum.SUBSCRIBER) {
    if (serviceFlags?.includes("bt_iap")) {
      return VisitorCatEnum.IAP_SUBSCRIBER; // IAP Subscriber of BT
    }

    return VisitorCatEnum.SUBSCRIBER; // Subscriber of BT
  }

  if (userType === UserTypeEnum.REGISTERED) {
    if (serviceFlags?.includes("reg_bt")) {
      if (registrationStatus === UserRegistrationStatusEnum.VERIFIED)
        return VisitorCatEnum.REGISTERED_VERIFIED; // Email Verified Registered User

      return VisitorCatEnum.REGISTERED_UNVERIFIED; // Email UnVerified Registered User
    }

    return VisitorCatEnum.REGISTERED; // Registered user (Subscriber of other pubs)
  }

  // Anonymous user
  return VisitorCatEnum.ANONYMOUS;
};

/**
 * Helper function to get the permutive visitor category value.
 * @param visitorcat The visitor category value
 * @returns {string} The permutive visitor category value
 */
export const getPermutiveVisitorCat = (visitorcat: number): string => {
  switch (visitorcat) {
    case 2:
      return "subscriber";

    case 3:
      return "registered";

    case 1:
    default:
      return "anonymous";
  }
};

/**
 * Helper function to get the permutive content type value.
 * @param contentType The content type value
 * @returns {string} The permutive content type value
 */
export const getPermutiveContentType = (
  contentType: "" | "1" | "2" | "3"
): string => {
  switch (contentType) {
    case "2":
      return "photo";

    case "3":
      return "video";

    case "1":
    default:
      return "article";
  }
};

/**
 * Helper function to get the permutive content category value.
 * @param contentcat The content category value
 * @returns {string} The permutive content category value
 */
export const getPermutiveContentCat = (contentcat: 1 | 2): string => {
  switch (contentcat) {
    case 2:
      return "premium";

    case 1:
    default:
      return "free";
  }
};
